import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton
} from '@mui/material';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { api } from '../../services/api';
import { Link } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';

interface NonCustodialExchange {
  exchangeId: number;
  extId: string;
  fromAssetId: number;
  toAssetId: number;
  fromAmount: string;
  toAmount: string;
  rate: string;
  status: string;
  withdrawalAddress: string;
  blockchainName: string;
  depositTxHash: string;
  createdAt: string;
  updatedAt: string;
  depositId: number;
  extIdImex: string;
  fromAssetCode: string;
  fromAssetName: string;
  toAssetCode: string;
  toAssetName: string;
  blockchainCode: string; // Add this
  withdrawal?: {
    withdrawalId: number;
    blockchainId: number;
    blockchainName: string;
    blockchainCode: string; // Add this
    assetId: number;
    assetCode: string;
    assetName: string;
    blockchainAssetId: number;
    exchangeId: number;
    type: string;
    status: string;
    extUid: string;
    createdAt: string;
    updatedAt: string;
  };
  deposit?: {
    depositId: number;
    blockchainId: number;
    blockchainName: string;
    blockchainCode: string; // Add this
    blockchainAssetId: number;
    blockchainAssetCode: string;
    blockchainAssetName: string;
    txHash: string;
    type: string;
    status: string;
    extUid: string;
    messageId: string;
    amount: string;
    imexDepositAddress: string;
    createdAt: string;
    updatedAt: string;
    destinationTxHash: string;
    transactionId: number;
  };
}

// Add a helper function to safely format dates
const formatDate = (dateString: string | null) => {
  if (!dateString) return '-';
  try {
    return format(new Date(dateString), 'dd.MM.yyyy HH:mm', { locale: ru });
  } catch (error) {
    console.error('Error formatting date:', dateString, error);
    return '-';
  }
};

const NonCustodyExchangesPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exchanges, setExchanges] = useState<NonCustodialExchange[]>([]);  // Initialize as empty array
  const [tabValue, setTabValue] = useState(0);
  const [selectedExchange, setSelectedExchange] = useState<NonCustodialExchange | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    fetchExchanges();
  }, []);

  const fetchExchanges = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/v1/admin-panel/exchanges/non-custody', {
        headers: { 'x-hasura-admin-secret': 'admin_secret_key' }
      });

      // The data is nested twice, so we need to access it correctly
      if (response.data?.success && response.data?.data?.data?.exchanges) {
        setExchanges(response.data.data.data.exchanges);
      } else {
        setExchanges([]); // Set empty array if no data
      }
    } catch (err: any) {
      console.error('Error fetching exchanges:', err);
      setError(err.response?.data?.message || err.message || 'Error loading exchanges');
      setExchanges([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const renderStatus = (status: string) => {
    switch (status) {
      case 'completed':
        return <Chip label="Завершен" color="success" size="small" />;
      case 'created':
        return <Chip label="Создан" color="info" size="small" />;
      case 'processing':
        return <Chip label="В обработке" color="warning" size="small" />;
      case 'failed':
        return <Chip label="Неуспешен" color="error" size="small" />;
      default:
        return <Chip label={status} color="default" size="small" />;
    }
  };

  const handleViewDetails = (exchange: NonCustodialExchange) => {
    setSelectedExchange(exchange);
    setDetailsOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Обмены" />
          <Tab label="Депозиты" />
          <Tab label="Выводы" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {tabValue === 0 && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Из</TableCell>
                    <TableCell>В</TableCell>
                    <TableCell>Сумма</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Дата создания</TableCell>
                    <TableCell>Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {exchanges && exchanges.length > 0 ? (
                    exchanges.map((exchange) => (
                      <TableRow key={exchange.extId}>
                        <TableCell>{exchange.extId}</TableCell>
                        <TableCell>
                          {exchange.fromAssetName} ({exchange.fromAssetCode})
                        </TableCell>
                        <TableCell>
                          {exchange.toAssetName} ({exchange.toAssetCode})
                        </TableCell>
                        <TableCell>{exchange.fromAmount}</TableCell>
                        <TableCell>{renderStatus(exchange.status)}</TableCell>
                        <TableCell>
                          {formatDate(exchange.createdAt)}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleViewDetails(exchange)}
                          >
                            Детали
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Alert severity="info">Нет обменов</Alert>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tabValue === 1 && ( // Deposits tab
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Обмен</TableCell> {/* New column */}
                    <TableCell>TX Hash</TableCell>
                    <TableCell>Сумма</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Дата создания</TableCell>
                    <TableCell>Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {exchanges && exchanges.length > 0 ? (
                    exchanges
                      .filter(exchange => exchange.deposit)
                      .map(exchange => (
                        <TableRow key={exchange.deposit!.depositId}>
                          <TableCell>{exchange.deposit!.depositId}</TableCell>
                          <TableCell>
                            {`${exchange.fromAmount} ${exchange.fromAssetCode} → ${exchange.toAmount || '?'} ${exchange.toAssetCode}`}
                          </TableCell>
                          <TableCell>{exchange.deposit!.txHash}</TableCell>
                          <TableCell>{exchange.deposit!.amount}</TableCell>
                          <TableCell>{renderStatus(exchange.deposit!.status)}</TableCell>
                          <TableCell>{formatDate(exchange.deposit!.createdAt)}</TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleViewDetails(exchange)}
                            >
                              Детали обмена
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Alert severity="info">Нет депозитов</Alert>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tabValue === 2 && ( // Withdrawals tab
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Обмен</TableCell> {/* New column */}
                    <TableCell>External ID</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Дата создания</TableCell>
                    <TableCell>Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {exchanges && exchanges.length > 0 ? (
                    exchanges
                      .filter(exchange => exchange.withdrawal)
                      .map(exchange => (
                        <TableRow key={exchange.withdrawal!.withdrawalId}>
                          <TableCell>{exchange.withdrawal!.withdrawalId}</TableCell>
                          <TableCell>
                            {`${exchange.fromAmount} ${exchange.fromAssetCode} → ${exchange.toAmount || '?'} ${exchange.toAssetCode}`}
                          </TableCell>
                          <TableCell>{exchange.extId}</TableCell>
                          <TableCell>{renderStatus(exchange.status)}</TableCell>
                          <TableCell>{formatDate(exchange.createdAt)}</TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleViewDetails(exchange)}
                            >
                              Детали обмена
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Alert severity="info">Нет выводов</Alert>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Paper>

      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {tabValue === 2 ? 'Детали вывода' : 'Детали обмена'} #{selectedExchange?.withdrawal?.withdrawalId || selectedExchange?.exchangeId}
          <IconButton
            onClick={() => setDetailsOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedExchange && (
            <Box>
              <Table size="small">
                <TableBody>
                  {/* Show exchange info if viewing from withdrawals tab */}
                  {tabValue === 2 && (
                    <>
                      <TableRow>
                        <TableCell colSpan={2} sx={{ pt: 3 }}>
                          <strong>Информация об обмене</strong>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>ID обмена</TableCell>
                        <TableCell>{selectedExchange.exchangeId}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>External ID</TableCell>
                        <TableCell>{selectedExchange.extId}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Из</TableCell>
                        <TableCell>
                          {selectedExchange.fromAmount} {selectedExchange.fromAssetCode}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>В</TableCell>
                        <TableCell>
                          {selectedExchange.toAmount} {selectedExchange.toAssetCode}
                        </TableCell>
                      </TableRow>
                    </>
                  )}

                  {/* Withdrawal details */}
                  {selectedExchange.withdrawal && (
                    <>
                      <TableRow>
                        <TableCell colSpan={2} sx={{ pt: 3 }}>
                          <strong>Информация о выводе</strong>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>ID вывода</TableCell>
                        <TableCell>{selectedExchange.withdrawal.withdrawalId}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>External ID</TableCell>
                        <TableCell>{selectedExchange.withdrawal.extUid}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Блокчейн</TableCell>
                        <TableCell>{selectedExchange.withdrawal.blockchainName} ({selectedExchange.withdrawal.blockchainCode})</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Актив</TableCell>
                        <TableCell>{selectedExchange.withdrawal.assetName} ({selectedExchange.withdrawal.assetCode})</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Тип</TableCell>
                        <TableCell>{selectedExchange.withdrawal.type}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Статус</TableCell>
                        <TableCell>{renderStatus(selectedExchange.withdrawal.status)}</TableCell>
                      </TableRow>
                    </>
                  )}

                  {/* Deposit details if not on withdrawals tab */}
                  {tabValue !== 2 && selectedExchange.deposit && (
                    <>
                      <TableRow>
                        <TableCell colSpan={2} sx={{ pt: 3 }}>
                          <strong>Информация о депозите</strong>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>ID депозита</TableCell>
                        <TableCell>{selectedExchange.deposit.depositId}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>TX Hash</TableCell>
                        <TableCell>{selectedExchange.deposit.txHash}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Блокчейн</TableCell>
                        <TableCell>{selectedExchange.deposit.blockchainName} ({selectedExchange.deposit.blockchainCode})</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Актив</TableCell>
                        <TableCell>{selectedExchange.deposit.blockchainAssetName} ({selectedExchange.deposit.blockchainAssetCode})</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Сумма</TableCell>
                        <TableCell>{selectedExchange.deposit.amount}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Статус</TableCell>
                        <TableCell>{renderStatus(selectedExchange.deposit.status)}</TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default NonCustodyExchangesPage;
